// LayoutNodePickerModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { tokenizeSearchQuery } from 'services/search/SearchIndexService';
import { searchBaseNodes } from 'services/search/BaseNodeSearchService';

// CSS
import './LayoutNodePickerModal.css';

// Components
import {
	getPageNodePresentationLabel,
	normalizePageNodePresentation,
	PAGE_NODE_PRESENTATION,
} from './LayoutNodePresentation';
import PageNodePreview from './PageNodePreview';
import Modal from 'components/modal/Modal';
import TextInput from 'components/textinput/TextInput';

const PRESENTATION_OPTIONS = [
	PAGE_NODE_PRESENTATION.LIST,
	PAGE_NODE_PRESENTATION.GALLERY,
	PAGE_NODE_PRESENTATION.FEED,
];

/**
 * LayoutNodePickerModal
 *
 * @returns {ReactNode} The rendered component.
 */
const LayoutNodePickerModal = React.memo(({
	initialPresentation = PAGE_NODE_PRESENTATION.LIST,
	isOpen,
	onClose,
	onSelect,
	baseKey,
}) => {

	// ----- CONTEXT ----- //

	const { theme } = useTheme();
	// ----- DEVICE DETECTION ----- //

	const isMobile = useIsMobile();

	// ----- STATE ----- //

	const [query, setQuery] = useState('');
	const [rows, setRows] = useState([]);
	const [searching, setSearching] = useState(false);
	const [searchError, setSearchError] = useState('');
	const [selectingNodeKey, setSelectingNodeKey] = useState('');
	const [presentation, setPresentation] = useState(normalizePageNodePresentation(initialPresentation));

	// ----- MEMOS ----- //

	const searchTokens = useMemo(() => tokenizeSearchQuery(query), [query]);
	const queryToken = searchTokens.length ? searchTokens[searchTokens.length - 1] : '';
	const noResults = Boolean(queryToken) && !searching && !rows.length && !searchError;

	// ----- EFFECTS ----- //

	useEffect(() => {
		if (!isOpen) return;

		setQuery('');
		setRows([]);
		setSearching(false);
		setSearchError('');
		setSelectingNodeKey('');
		setPresentation(normalizePageNodePresentation(initialPresentation));
	}, [initialPresentation, isOpen]);

	useEffect(() => {
		if (!isOpen || !baseKey || !queryToken) {
			setRows([]);
			setSearching(false);
			setSearchError('');
			return undefined;
		}

		let cancelled = false;

		setSearching(true);
		setSearchError('');

		const handle = setTimeout(async () => {
			try {
				const resultRows = await searchBaseNodes({
					max: 30,
					rawQuery: query,
					baseKey,
				});

				if (cancelled) return;

				const filtered = (Array.isArray(resultRows) ? resultRows : [])
					.filter((row) => row?.key)
					.sort((left, right) => String(left?.title || '').localeCompare(String(right?.title || '')));

				setRows(filtered);
			} catch (error) {
				if (cancelled) return;
				setRows([]);
				setSearchError(error?.message || 'Failed to search nodes.');
			} finally {
				if (!cancelled) setSearching(false);
			}
		}, 220);

		return () => {
			cancelled = true;
			clearTimeout(handle);
		};
	}, [isOpen, query, queryToken, baseKey]);

	/**
	 * handleSelectRow
	 *
	 * Handles select row.
	 *
	 * @param {object} row - row.
	 * @returns {Promise<void>} Resolves when the handler completes.
	 */
	async function handleSelectRow(row) {
		if (!row?.key) return;

		setSelectingNodeKey(row.key);

		try {
			await onSelect?.({
				node: row,
				nodeKey: row.key,
				presentation,
			});
			onClose?.();
		} finally {
			setSelectingNodeKey('');
		}
	}
	// ----- RENDER ----- //

	return (
		<Modal
			height="80%"
			isOpen={isOpen}
			title="Add Data"
			width={isMobile ? '100%' : 760}
			onClose={onClose}
		>
			<div className="page-node-picker-modal-container">

				{/* SEARCH */}
				<div className="page-node-picker-modal-search">
					<TextInput
						autoFocus
						placeholder="Search all collections"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
					/>
				</div>

				{/* PRESENTATION ROW */}
				<div className="page-node-picker-modal-presentation-row">
					{PRESENTATION_OPTIONS.map((option) => {
						const isSelected = presentation === option;

						return (
							<button
								className="page-node-picker-modal-presentation-button"
								style={{
									backgroundColor: isSelected ? theme.backgroundHover : theme.background,
									borderColor: isSelected ? theme.foregroundMuted : theme.backgroundMuted,
									color: isSelected ? theme.foreground : theme.foregroundMuted,
								}}
								key={option}
								type="button"
								onClick={() => setPresentation(option)}
							>
								{getPageNodePresentationLabel(option)}
							</button>
						);
					})}
				</div>

				{/* STATUS */}
				{(searching || searchError || noResults) ? (
					<div
						className="page-node-picker-modal-status"
					>
						{searching
							? 'Searching...'
							: (searchError || (noResults ? 'No results found.' : ''))}
					</div>
				) : null}

				{/* RESULTS GALLERY */}
				<div
					className={[
						'page-node-picker-modal-results',
						presentation === PAGE_NODE_PRESENTATION.GALLERY
							? 'page-node-picker-modal-results-gallery'
							: 'page-node-picker-modal-results-list',
					].join(' ')}
				>
					{rows.map((row) => {
						const isSelecting = selectingNodeKey === row.key;

						return (
							<button
								className="page-node-picker-modal-result-button"
								style={{
									opacity: isSelecting ? 0.6 : 1,
								}}
								key={row.key}
								type="button"
								onClick={() => handleSelectRow(row)}
							>
								<PageNodePreview
									node={row}
									presentation={presentation}
								/>
							</button>
						);
					})}
				</div>
			</div>
		</Modal>
	);
});

// Dev Tools
LayoutNodePickerModal.displayName = 'LayoutNodePickerModal';

export default LayoutNodePickerModal;
